// Ad conversion tracking (Google Ads + Meta Pixel)
//
// This is intentionally dormant until you add the IDs below to your environment.
// With no IDs set, every function here is a safe no-op — nothing loads, nothing fires.
// That means it can ship to production now and "turn on" the moment you create the
// ad accounts and set the env vars (no code change needed).
//
// Env vars (set in .env / Vercel — all optional, baked in at build time by CRA):
//   REACT_APP_GOOGLE_ADS_ID            e.g. "AW-1234567890"   (Google Ads account conversion ID)
//   REACT_APP_GOOGLE_ADS_PURCHASE_LABEL e.g. "abCdEfGhIj"     (the conversion action's label)
//   REACT_APP_META_PIXEL_ID            e.g. "1234567890123456" (Meta/Facebook Pixel ID)

const GOOGLE_ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || '';
const GOOGLE_ADS_PURCHASE_LABEL = process.env.REACT_APP_GOOGLE_ADS_PURCHASE_LABEL || '';
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '';

let metaPixelLoaded = false;

// Inject the Meta Pixel base code once (loads fbevents.js + fires the initial PageView).
const loadMetaPixel = () => {
  if (metaPixelLoaded || !META_PIXEL_ID || typeof window === 'undefined') return;
  if (window.fbq) {
    metaPixelLoaded = true;
    return;
  }

  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');
  metaPixelLoaded = true;
};

// Call once on app start (alongside initGA). Safe to call when nothing is configured.
export const initConversionTracking = () => {
  if (typeof window === 'undefined') return;

  // Register the Google Ads account with gtag so conversion events have a destination.
  // gtag itself is already loaded by index.html (shared with GA4).
  if (GOOGLE_ADS_ID && window.gtag) {
    window.gtag('config', GOOGLE_ADS_ID);
  }

  loadMetaPixel();
};

// Fire a purchase/conversion across every configured ad platform.
// Pass the order's transaction id, value (in dollars), currency, and optional line items.
export const trackConversion = ({
  transactionId = '',
  value = 0,
  currency = 'USD',
  items = [],
} = {}) => {
  if (typeof window === 'undefined') return;

  const numericValue = Number(value) || 0;
  const upperCurrency = (currency || 'USD').toUpperCase();

  // --- Google Ads conversion ---
  if (GOOGLE_ADS_ID && GOOGLE_ADS_PURCHASE_LABEL && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_PURCHASE_LABEL}`,
      value: numericValue,
      currency: upperCurrency,
      transaction_id: transactionId, // dedupes repeated success-page loads
    });
  }

  // --- Meta Pixel Purchase ---
  if (META_PIXEL_ID && window.fbq) {
    window.fbq(
      'track',
      'Purchase',
      {
        value: numericValue,
        currency: upperCurrency,
        contents: items.map((item) => ({
          id: item.id || item.sku,
          quantity: item.quantity || 1,
          item_price: item.price,
        })),
        num_items: items.reduce((sum, item) => sum + (item.quantity || 1), 0),
      },
      // Event ID lets Meta dedupe if you later add server-side Conversions API.
      transactionId ? { eventID: transactionId } : undefined
    );
  }
};

export default {
  initConversionTracking,
  trackConversion,
};
